import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Alert, CircularProgress, Tooltip, InputAdornment, Autocomplete,
} from '@mui/material';
import {
  Add, Edit, Delete, FileUpload, FileDownload, Search,
  WhatsApp, Refresh, Close, Save, FilterList,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const cfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const EMPTY = { nombre: '', telefono: '', email: '', categorias: [], notas: '' };

// ── Chip de categoría con color hash ─────────────────────────────────────────
function CatChip({ label, onDelete }) {
  const hue = Math.abs([...label].reduce((h, c) => h * 31 + c.charCodeAt(0), 0)) % 360;
  return (
    <Chip
      label={label} size="small"
      onDelete={onDelete}
      sx={{ bgcolor: `hsl(${hue},50%,90%)`, color: `hsl(${hue},40%,30%)`, fontWeight: 600, fontSize: 11 }}
    />
  );
}

export default function ContactosWA() {
  const [contactos, setContactos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [buscar, setBuscar] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [nuevaCat, setNuevaCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importando, setImportando] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { cargarTodo(); }, []);

  const cargarTodo = async () => {
    try {
      setLoading(true);
      const [cRes, catRes] = await Promise.all([
        axios.get(`${API}/api/whatsapp/contactos`, cfg()),
        axios.get(`${API}/api/whatsapp/contactos/categorias`, cfg()),
      ]);
      setContactos(cRes.data.data || []);
      setCategorias(catRes.data.data || []);
    } catch (e) {
      setError('Error al cargar contactos');
    } finally { setLoading(false); }
  };

  const cargarFiltrado = async () => {
    try {
      const params = new URLSearchParams();
      if (buscar) params.set('buscar', buscar);
      if (filtroCategoria) params.set('categoria', filtroCategoria);
      const r = await axios.get(`${API}/api/whatsapp/contactos?${params}`, cfg());
      setContactos(r.data.data || []);
    } catch { setError('Error al filtrar'); }
  };

  useEffect(() => { cargarFiltrado(); }, [buscar, filtroCategoria]);

  const abrirNuevo = () => { setForm(EMPTY); setEditando(null); setDialogOpen(true); };
  const abrirEditar = (c) => { setForm({ ...c }); setEditando(c.id); setDialogOpen(true); };

  const guardar = async () => {
    if (!form.telefono) return setError('El teléfono es requerido');
    // Normalizar teléfono: solo dígitos, sin + inicial para Meta
    const tel = form.telefono.replace(/\D/g, '');
    if (tel.length < 7) return setError('Teléfono inválido');
    try {
      setSaving(true);
      const payload = { ...form, telefono: tel };
      if (editando) {
        await axios.put(`${API}/api/whatsapp/contactos/${editando}`, payload, cfg());
        setSuccess('Contacto actualizado');
      } else {
        await axios.post(`${API}/api/whatsapp/contactos`, payload, cfg());
        setSuccess('Contacto creado');
      }
      setDialogOpen(false);
      cargarTodo();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const eliminar = async () => {
    try {
      await axios.delete(`${API}/api/whatsapp/contactos/${confirmEliminar.id}`, cfg());
      setConfirmEliminar(null);
      setSuccess('Contacto eliminado');
      cargarTodo();
      setTimeout(() => setSuccess(null), 3000);
    } catch { setError('Error al eliminar'); }
  };

  const agregarCategoria = () => {
    const cat = nuevaCat.trim();
    if (!cat || form.categorias.includes(cat)) return;
    setForm(f => ({ ...f, categorias: [...f.categorias, cat] }));
    setNuevaCat('');
  };

  // ── Importar CSV ─────────────────────────────────────────────────────────
  const leerCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.replace(/["\r]/g, '').trim().toLowerCase());
      const rows = lines.slice(1).map(line => {
        const cols = line.match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (cols[i] || '').replace(/["\r]/g, '').trim(); });
        return {
          nombre: obj.nombre || '',
          telefono: obj.telefono || obj.phone || obj['teléfono'] || '',
          email: obj.email || obj['correo'] || '',
          categorias: obj.categorias ? obj.categorias.split('|').map(c => c.trim()).filter(Boolean) : [],
          notas: obj.notas || obj.notes || '',
        };
      }).filter(r => r.telefono);
      setImportPreview(rows);
      setImportDialog(true);
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const ejecutarImport = async (sobreescribir = false) => {
    try {
      setImportando(true);
      const r = await axios.post(`${API}/api/whatsapp/contactos/importar`, { contactos: importPreview, sobreescribir }, cfg());
      setImportDialog(false);
      setImportPreview([]);
      setSuccess(`Importados: ${r.data.importados} | Duplicados: ${r.data.duplicados} | Errores: ${r.data.errores}`);
      cargarTodo();
      setTimeout(() => setSuccess(null), 5000);
    } catch (e) {
      setError(e.response?.data?.message || 'Error al importar');
    } finally { setImportando(false); }
  };

  // ── Exportar CSV ──────────────────────────────────────────────────────────
  const exportar = async () => {
    try {
      const params = filtroCategoria ? `?categoria=${filtroCategoria}` : '';
      const r = await axios.get(`${API}/api/whatsapp/contactos/exportar${params}`, {
        ...cfg(), responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'contactos_whatsapp.csv';
      a.click(); URL.revokeObjectURL(url);
    } catch { setError('Error al exportar'); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#25d366' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1b5e20' }}>
            <WhatsApp sx={{ mr: 1, verticalAlign: 'middle', color: '#25d366' }} />
            Contactos WhatsApp
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {contactos.length} contacto(s) · {categorias.length} categoría(s)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={leerCSV} />
          <Button size="small" startIcon={<FileUpload />} onClick={() => fileRef.current.click()}
            sx={{ textTransform: 'none', borderColor: '#25d366', color: '#1da851', border: '1px solid' }}>
            Importar CSV
          </Button>
          <Button size="small" startIcon={<FileDownload />} onClick={exportar}
            sx={{ textTransform: 'none', borderColor: '#25d366', color: '#1da851', border: '1px solid' }}>
            Exportar
          </Button>
          <Button size="small" variant="contained" startIcon={<Add />} onClick={abrirNuevo}
            sx={{ textTransform: 'none', bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' } }}>
            Nuevo contacto
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Buscar por nombre, teléfono o email..."
          value={buscar} onChange={e => setBuscar(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
        />
        <TextField
          select size="small" label="Categoría" value={filtroCategoria}
          onChange={e => setFiltroCategoria(e.target.value)} sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {categorias.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <IconButton size="small" onClick={cargarTodo} title="Actualizar">
          <Refresh fontSize="small" />
        </IconButton>
      </Box>

      {/* Plantilla CSV */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          Formato CSV para importar: <code>nombre,telefono,email,categorias,notas</code> — Las categorías van separadas por <code>|</code> (ej: <code>Clientes|VIP</code>)
        </Typography>
      </Box>

      {/* Tabla */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#e8f5e9', fontWeight: 700, fontSize: 12 } }}>
                <TableCell>Nombre</TableCell>
                <TableCell>Teléfono</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Categorías</TableCell>
                <TableCell>Notas</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contactos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary', fontSize: 13 }}>
                    Sin contactos — importa un CSV o agrega manualmente
                  </TableCell>
                </TableRow>
              ) : contactos.map(c => (
                <TableRow key={c.id} hover sx={{ opacity: c.activo === false ? 0.5 : 1 }}>
                  <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{c.nombre || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WhatsApp sx={{ fontSize: 14, color: '#25d366' }} />
                      {c.telefono}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{c.email || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(c.categorias || []).map(cat => <CatChip key={cat} label={cat} />)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.notas || '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => abrirEditar(c)}>
                        <Edit sx={{ fontSize: 15, color: '#1565c0' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => setConfirmEliminar(c)}>
                        <Delete sx={{ fontSize: 15, color: '#ef5350' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          {editando ? 'Editar contacto' : 'Nuevo contacto'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField fullWidth size="small" label="Nombre"
              value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
            <TextField fullWidth size="small" label="Teléfono *"
              value={form.telefono} onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
              helperText="Incluye código de país sin el + (ej: 593987654321)" />
            <TextField fullWidth size="small" label="Email"
              value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            {/* Categorías */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>Categorías</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {(form.categorias || []).map(cat => (
                  <CatChip key={cat} label={cat} onDelete={() => setForm(f => ({ ...f, categorias: f.categorias.filter(c => c !== cat) }))} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Autocomplete
                  freeSolo size="small" options={categorias}
                  inputValue={nuevaCat} onInputChange={(_, v) => setNuevaCat(v)}
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} placeholder="Nueva categoría..." size="small" />}
                />
                <Button size="small" variant="outlined" onClick={agregarCategoria} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                  Agregar
                </Button>
              </Box>
            </Box>
            <TextField fullWidth size="small" label="Notas" multiline rows={2}
              value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving || !form.telefono}
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, textTransform: 'none' }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminar */}
      <Dialog open={!!confirmEliminar} onClose={() => setConfirmEliminar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>Eliminar contacto</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13 }}>
            ¿Eliminar a <strong>{confirmEliminar?.nombre || confirmEliminar?.telefono}</strong>? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmEliminar(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminar} sx={{ textTransform: 'none' }}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog importar CSV */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>
          Importar {importPreview.length} contactos desde CSV
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, bgcolor: '#f5f5f5' } }}>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Teléfono</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Categorías</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importPreview.slice(0, 20).map((c, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontSize: 12 }}>{c.nombre}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{c.telefono}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{c.email}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{(c.categorias || []).join(', ')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {importPreview.length > 20 && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 1 }}>
              Mostrando 20 de {importPreview.length} registros
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setImportDialog(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="outlined" onClick={() => ejecutarImport(false)} disabled={importando} sx={{ textTransform: 'none' }}>
            Importar (saltar duplicados)
          </Button>
          <Button variant="contained" onClick={() => ejecutarImport(true)} disabled={importando}
            startIcon={importando ? <CircularProgress size={16} /> : <FileUpload />}
            sx={{ bgcolor: '#25d366', '&:hover': { bgcolor: '#1da851' }, textTransform: 'none' }}>
            Importar (sobreescribir duplicados)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
